import React, { useState } from 'react';

interface InputFieldProps {
    label: string;
    value: string;
    type?: string;
    placeholder?: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    onFocus?: () => void;
    error?: string;
    /** Shows a value the user can't change here, e.g. an email they already gave. */
    readOnly?: boolean;
}

const InputField: React.FC<InputFieldProps> = ({
    label,
    value,
    type = 'text',
    placeholder,
    onChange,
    onBlur,
    onFocus: onFocusProp,
    error,
    readOnly = false
}) => {
    const [showPassword, setShowPassword] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const isPassword = type === 'password';
    const inputType = isPassword && showPassword ? 'text' : type;

    return (
        <div
            style={{
                width: '100%',
                marginBottom: error ? 4 : 16,
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <div
                style={{
                    fontFamily: 'Manrope',
                    borderRadius: 12,
                    border: error 
                        ? '1.5px solid #EF4444' 
                        : isFocused 
                            ? '1.5px solid #FF5212' 
                            : '1px solid #D1D5DB',
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '12px 16px',
                    backgroundColor: readOnly ? '#F3F4F6' : '#FFFFFF',
                    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                    boxShadow: isFocused ? '0 0 0 3px rgba(255, 82, 18, 0.1)' : 'none'
                }}
            >
                <label 
                    style={{ 
                        fontSize: 13, 
                        color: '#6B7280',
                        marginBottom: 4,
                        fontWeight: 500
                    }}
                >
                    {label}
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                        type={inputType}
                        placeholder={placeholder}
                        value={value}
                        readOnly={readOnly}
                        onChange={onChange}
                        onBlur={(e) => {
                            setIsFocused(false);
                            onBlur?.(e);
                        }}
                        onFocus={() => {
                            setIsFocused(true);
                            onFocusProp?.();
                        }}
                        style={{
                            flex: 1,
                            fontFamily: 'Manrope',
                            border: 'none',
                            outline: 'none',
                            fontSize: 16,
                            color: readOnly ? '#6B7280' : '#111827',
                            padding: 0,
                            margin: 0,
                            background: 'transparent'
                        }}
                        className="manrope-semibold"
                        aria-invalid={error ? 'true' : 'false'}
                        aria-describedby={error ? `${label}-error` : undefined}
                    />
                    {isPassword && (
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '4px 8px',
                                display: 'flex',
                                alignItems: 'center',
                                color: '#6B7280',
                                fontSize: 14
                            }}
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                            {showPassword ? 'Hide' : 'Show'}
                        </button>
                    )}
                </div>
            </div>
            {error && (
                <span
                    id={`${label}-error`}
                    role="alert"
                    style={{
                        color: '#EF4444',
                        fontSize: 13,
                        marginTop: 4,
                        marginLeft: 4,
                        fontWeight: 400
                    }}
                >
                    {error}
                </span>
            )}
        </div>
    );
};

export default InputField;
